'use client';

import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowUpDownIcon, Add01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { Badge } from '../../components/badge';
import { createSelectListCollection, Select } from '../../components/select';
import { Text } from '../../components/text';
import { Button } from '../../components/button';

export interface RoleSelectionDropdownProps {
  value: string;
  customRoles?: ApplicationRoleDto[];
  onValueChange: (value: string, roleId?: string | null) => void;
  onCreateRole?: () => void;
  disabled?: boolean;
  fallbackRoleName?: string;
}
const predefinedRoleOptions = createSelectListCollection({
  items: [
    { label: 'Admin', value: 'admin' },
    { label: 'User', value: 'user' },
    { label: 'Guest', value: 'guest' },
    { label: 'Developer', value: 'developer' },
    { label: 'Tester', value: 'tester' },
    { label: 'Manager', value: 'manager' },
    { label: 'Viewer', value: 'viewer' },
    { label: 'Editor', value: 'editor' },
    { label: 'Other', value: 'OTHER' },
  ],
});

export function RoleSelectionDropdown({
  value,
  customRoles = [],
  onValueChange,
  onCreateRole,
  disabled,
  fallbackRoleName,
}: RoleSelectionDropdownProps) {
  const t = useTranslations('designSystem.applicationRoles');
  const [internalValue, setInternalValue] = useState<string>('');

  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);
  const valueRoleId = internalValue?.startsWith('custom:')
    ? internalValue.replace('custom:', '')
    : null;
  const valueRoleInList = valueRoleId
    ? customRoles.find((r) => r.id === valueRoleId)
    : null;
  const fallbackRole =
    valueRoleId && !valueRoleInList && fallbackRoleName
      ? ({
          id: valueRoleId,
          name: fallbackRoleName,
          description: null,
        } as ApplicationRoleDto)
      : null;
  // Build combined options with custom roles first
  const customRoleOptions = customRoles.map((role) => ({
    label: role.name,
    value: `custom:${role.id}`,
    roleId: role.id,
    isCustom: true,
  }));

  // Add fallback role if we have a roleId but the role isn't in the list
  if (fallbackRole) {
    customRoleOptions.push({
      label: fallbackRole.name,
      value: `custom:${fallbackRole.id}`,
      roleId: fallbackRole.id,
      isCustom: true,
    });
  }

  const allOptions = [
    ...customRoleOptions,
    { label: '──────────', value: 'separator', disabled: true },
    ...predefinedRoleOptions.items.map((item) => ({
      ...item,
      isCustom: false,
    })),
  ];
  const collectionItems = allOptions.filter(
    (item) => item.value !== 'separator',
  );
  const collection = createSelectListCollection({
    items: collectionItems,
  });
  const handleValueChange = (selectedValue: string[]) => {
    const val = selectedValue[0];

    if (!val) return;
    setInternalValue(val);
    if (val.startsWith('custom:')) {
      const roleId = val.replace('custom:', '');
      const role = customRoles.find((r) => r.id === roleId);

      onValueChange(role?.name || val, roleId);
    } else {
      onValueChange(val, null);
    }
  };

  const selectedCustomRole =
    internalValue && internalValue.startsWith('custom:')
      ? customRoles.find(
          (r) => r.id === internalValue.replace('custom:', ''),
        ) || fallbackRole
      : customRoles.find((r) => r.name === internalValue);
  const normalizedValue = (() => {
    if (!internalValue) return '';
    if (internalValue.startsWith('custom:')) {
      const roleId = internalValue.replace('custom:', '');
      const roleExists = customRoles.some((r) => r.id === roleId);

      return roleExists ? internalValue : internalValue;
    }

    if (internalValue === 'OTHER') return 'OTHER';
    const existsInCollection = collectionItems.some(
      (item) => item.value === internalValue,
    );

    return existsInCollection ? internalValue : '';
  })();
  const selectValue = normalizedValue ? [normalizedValue] : [];

  return (
    <VStack alignItems="stretch" gap={2}>
      <Select.Root
        positioning={{ sameWidth: true }}
        value={selectValue}
        collection={collection}
        onValueChange={(e) => {
          handleValueChange(e.value);
        }}
        disabled={disabled}
      >
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText>
              {selectedCustomRole ? (
                <HStack gap={2} alignItems="center">
                  <Text>{selectedCustomRole.name}</Text>
                  <Badge size="sm" variant="outline">
                    {t('roleSelection.customRole')}
                  </Badge>
                </HStack>
              ) : normalizedValue && normalizedValue !== 'OTHER' ? (
                predefinedRoleOptions.items.find(
                  (item) => item.value === normalizedValue,
                )?.label || ''
              ) : null}
            </Select.ValueText>
            <ArrowUpDownIcon size={16} />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content maxH="320px" overflowY="auto">
            {customRoleOptions.length > 0 && (
              <Select.ItemGroup>
                <Box px={2} py={1}>
                  <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                    {t('roleSelection.customRoles')}
                  </Text>
                </Box>
                {customRoleOptions.map((option) => {
                  const collectionItem = collectionItems.find(
                    (item) => item.value === option.value,
                  );

                  return collectionItem ? (
                    <Select.Item key={option.value} item={collectionItem}>
                      <Select.ItemText>
                        <HStack gap={2} alignItems="center">
                          <Text>{option.label}</Text>
                          <Badge size="sm" variant="outline">
                            {t('roleSelection.customRole')}
                          </Badge>
                        </HStack>
                      </Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ) : null;
                })}
              </Select.ItemGroup>
            )}
            <Select.ItemGroup>
              {customRoleOptions.length > 0 && (
                <Box px={2} py={1}>
                  <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                    {t('roleSelection.predefinedRoles')}
                  </Text>
                </Box>
              )}
              {predefinedRoleOptions.items.map((option) => {
                const collectionItem = collectionItems.find(
                  (item) => item.value === option.value,
                );

                return collectionItem ? (
                  <Select.Item key={option.value} item={collectionItem}>
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon size={16} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ) : null;
              })}
            </Select.ItemGroup>
            {onCreateRole && (
              <Box
                px={2}
                py={1}
                borderTopWidth="1px"
                borderColor="border.default"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  w="full"
                  justifyContent="flex-start"
                  onClick={onCreateRole}
                >
                  <Add01Icon size={14} />
                  {t('roleSelection.createCustomRole')}
                </Button>
              </Box>
            )}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </VStack>
  );
}
